"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ZodJsonSchema = exports.ZodJsonSchemaTarget = void 0;
const zod_1 = require("zod");
var ZodJsonSchemaTarget;
(function (ZodJsonSchemaTarget) {
    ZodJsonSchemaTarget["JsonSchema7"] = "json_schema_7";
    ZodJsonSchemaTarget["OpenAi"] = "open_ai";
    ZodJsonSchemaTarget["OpenAiSdkHelper"] = "open_ai_sdk_helper";
})(ZodJsonSchemaTarget || (exports.ZodJsonSchemaTarget = ZodJsonSchemaTarget = {}));
const OPEN_AI_ANY_TYPE = 'OpenAiAnyType';
const SUBSCHEMA_KEYWORDS = ['additionalProperties', 'items', 'not'];
const SUBSCHEMA_LIST_KEYWORDS = ['anyOf', 'allOf', 'prefixItems', 'items'];
const SUBSCHEMA_MAP_KEYWORDS = ['properties', '$defs', 'definitions'];
const TYPING_KEYWORDS = ['type', 'anyOf', '$ref', 'enum', 'const'];
const CHECK_NAME_FORMATS = new Set(['starts_with', 'ends_with', 'includes', 'regex']);
class ZodJsonSchema {
    static fromSchema(schema, options = {}) {
        const target = options.target ?? ZodJsonSchemaTarget.JsonSchema7;
        const openAi = target !== ZodJsonSchemaTarget.JsonSchema7;
        const document = zod_1.z.toJSONSchema(schema, {
            target: openAi ? 'draft-2020-12' : 'draft-07',
            io: 'input',
            reused: 'inline',
            unrepresentable: (ctx) => ZodJsonSchema.describeUnrepresentable(ctx),
            override: (ctx) => {
                ZodJsonSchema.closeStrippedObject(ctx);
                if (openAi) {
                    ZodJsonSchema.requireOptionalAsNullable(ctx, target === ZodJsonSchemaTarget.OpenAiSdkHelper);
                }
                else {
                    ZodJsonSchema.requireKeysThatRejectUndefined(ctx);
                }
            },
        });
        ZodJsonSchema.normalize(document);
        if (openAi) {
            ZodJsonSchema.restrictToOpenAiSubset(document);
        }
        return { ...document };
    }
    static parseFromOpenAi(schema, value) {
        return schema.parse(ZodJsonSchema.dropNullsMeaningOmitted(schema, value));
    }
    static safeParseFromOpenAi(schema, value) {
        return schema.safeParse(ZodJsonSchema.dropNullsMeaningOmitted(schema, value));
    }
    static describeUnrepresentable(ctx) {
        switch (ctx.zodSchema._zod.def.type) {
            case 'date':
                return { type: 'string', format: 'date-time' };
            case 'bigint':
                return { type: 'integer', format: 'int64' };
            case 'undefined':
            case 'void':
            case 'nan':
            case 'symbol':
            case 'function':
                return { not: {} };
            case 'set':
                return { type: 'array', uniqueItems: true };
            case 'map':
                return { type: 'array' };
            case 'custom':
                return ZodJsonSchema.acceptsOnlyFunctions(ctx.zodSchema) ? { not: {} } : 'any';
            default:
                return 'any';
        }
    }
    static acceptsOnlyFunctions(schema) {
        return (zod_1.z.safeParse(schema, () => undefined).success &&
            [null, {}, [], '', 0, false].every((value) => !zod_1.z.safeParse(schema, value).success));
    }
    static requireKeysThatRejectUndefined(ctx) {
        const def = ctx.zodSchema._zod.def;
        const properties = ctx.jsonSchema.properties;
        if (def.type !== 'object' || properties === undefined) {
            return;
        }
        const required = Object.entries(def.shape)
            .filter(([key, field]) => key in properties && !ZodJsonSchema.acceptsUndefined(field))
            .map(([key]) => key);
        if (required.length > 0) {
            ctx.jsonSchema.required = required;
        }
        else {
            delete ctx.jsonSchema.required;
        }
    }
    static dropNullsMeaningOmitted(schema, value) {
        if (schema instanceof zod_1.z.core.$ZodObject) {
            if (typeof value !== 'object' || value === null || Array.isArray(value)) {
                return value;
            }
            const cleaned = { ...value };
            for (const [key, field] of Object.entries(schema._zod.def.shape)) {
                if (cleaned[key] === null && !zod_1.z.safeParse(field, null).success && ZodJsonSchema.acceptsUndefined(field)) {
                    delete cleaned[key];
                }
                else if (key in cleaned) {
                    cleaned[key] = ZodJsonSchema.dropNullsMeaningOmitted(field, cleaned[key]);
                }
            }
            return cleaned;
        }
        if (schema instanceof zod_1.z.core.$ZodArray) {
            return Array.isArray(value) ?
                value.map((item) => ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.element, item))
                : value;
        }
        if (schema instanceof zod_1.z.core.$ZodTuple) {
            const { items, rest } = schema._zod.def;
            return Array.isArray(value) ?
                value.map((item, i) => ZodJsonSchema.dropNullsMeaningOmitted(items[i] ?? rest ?? zod_1.z.unknown(), item))
                : value;
        }
        if (schema instanceof zod_1.z.core.$ZodRecord) {
            if (typeof value !== 'object' || value === null || Array.isArray(value)) {
                return value;
            }
            return Object.fromEntries(Object.entries(value).map(([key, item]) => [
                key,
                ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.valueType, item),
            ]));
        }
        if (schema instanceof zod_1.z.core.$ZodUnion) {
            for (const option of schema._zod.def.options) {
                const cleaned = ZodJsonSchema.dropNullsMeaningOmitted(option, value);
                if (zod_1.z.safeParse(option, cleaned).success) {
                    return cleaned;
                }
            }
            return value;
        }
        if (schema instanceof zod_1.z.core.$ZodIntersection) {
            return ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.right, ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.left, value));
        }
        if (schema instanceof zod_1.z.core.$ZodPipe) {
            const { in: input, out } = schema._zod.def;
            return ZodJsonSchema.dropNullsMeaningOmitted(input instanceof zod_1.z.core.$ZodTransform ? out : input, value);
        }
        if (schema instanceof zod_1.z.core.$ZodLazy) {
            return ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.innerType, value);
        }
        if (schema instanceof zod_1.z.core.$ZodOptional ||
            schema instanceof zod_1.z.core.$ZodNullable ||
            schema instanceof zod_1.z.core.$ZodDefault ||
            schema instanceof zod_1.z.core.$ZodPrefault ||
            schema instanceof zod_1.z.core.$ZodNonOptional ||
            schema instanceof zod_1.z.core.$ZodReadonly ||
            schema instanceof zod_1.z.core.$ZodCatch) {
            return ZodJsonSchema.dropNullsMeaningOmitted(schema._zod.def.innerType, value);
        }
        return value;
    }
    static acceptsUndefined(field) {
        try {
            return zod_1.z.safeParse(field, undefined).success;
        }
        catch {
            return false;
        }
    }
    static closeStrippedObject(ctx) {
        const def = ctx.zodSchema._zod.def;
        if (def.type === 'object' && def.catchall === undefined && ctx.jsonSchema.additionalProperties === undefined) {
            ctx.jsonSchema.additionalProperties = false;
        }
    }
    static requireOptionalAsNullable(ctx, keepDefaultedNonNull) {
        const def = ctx.zodSchema._zod.def;
        const properties = ctx.jsonSchema.properties;
        if (def.type !== 'object' || properties === undefined) {
            return;
        }
        for (const [key, field] of Object.entries(def.shape)) {
            const property = properties[key];
            if (typeof property !== 'object') {
                continue;
            }
            if (ZodJsonSchema.matchesNothing(property)) {
                delete properties[key];
                continue;
            }
            if (!ZodJsonSchema.acceptsUndefined(field)) {
                continue;
            }
            if (keepDefaultedNonNull && (field._zod.def.type === 'default' || field._zod.def.type === 'prefault')) {
                continue;
            }
            const valueSchema = field._zod.def.type === 'optional' ? field._zod.def.innerType : field;
            if (!zod_1.z.safeParse(valueSchema, null).success) {
                properties[key] = { anyOf: [property, { type: 'null' }] };
            }
        }
        ctx.jsonSchema.required = Object.keys(properties);
    }
    static normalize(document) {
        ZodJsonSchema.forEachNode(document, (node) => {
            if (Array.isArray(node.oneOf)) {
                node.anyOf = node.oneOf;
                delete node.oneOf;
            }
            if (ZodJsonSchema.isPlainStringKeys(node.propertyNames)) {
                delete node.propertyNames;
            }
            delete node.readOnly;
            if (ZodJsonSchema.isNode(node.additionalProperties) && Object.keys(node.additionalProperties).length === 0) {
                node.additionalProperties = true;
            }
            if (typeof node.format === 'string' && CHECK_NAME_FORMATS.has(node.format) && 'pattern' in node) {
                delete node.format;
            }
        });
    }
    static restrictToOpenAiSubset(document) {
        const anyTypeRef = `#/$defs/${OPEN_AI_ANY_TYPE}`;
        let usesAnyType = false;
        const negated = new Set();
        ZodJsonSchema.forEachNode(document, (node) => {
            if (ZodJsonSchema.isNode(node.not)) {
                negated.add(node.not);
            }
            if (Array.isArray(node.anyOf)) {
                node.anyOf = node.anyOf.filter((branch) => !ZodJsonSchema.isNode(branch) || !ZodJsonSchema.matchesNothing(branch));
            }
            if (!negated.has(node) &&
                !ZodJsonSchema.matchesNothing(node) &&
                !TYPING_KEYWORDS.some((keyword) => keyword in node)) {
                node.$ref = anyTypeRef;
                usesAnyType = true;
            }
        });
        if (usesAnyType) {
            const defs = ZodJsonSchema.isNode(document.$defs) ? document.$defs : {};
            defs[OPEN_AI_ANY_TYPE] = {
                type: ['string', 'number', 'integer', 'boolean', 'array', 'null'],
                items: { $ref: anyTypeRef },
            };
            document.$defs = defs;
        }
    }
    static forEachNode(node, visit) {
        visit(node);
        for (const keyword of SUBSCHEMA_KEYWORDS) {
            const child = node[keyword];
            if (ZodJsonSchema.isNode(child)) {
                ZodJsonSchema.forEachNode(child, visit);
            }
        }
        for (const keyword of SUBSCHEMA_LIST_KEYWORDS) {
            const children = node[keyword];
            if (Array.isArray(children)) {
                children
                    .filter((child) => ZodJsonSchema.isNode(child))
                    .forEach((child) => ZodJsonSchema.forEachNode(child, visit));
            }
        }
        for (const keyword of SUBSCHEMA_MAP_KEYWORDS) {
            const children = node[keyword];
            if (ZodJsonSchema.isNode(children)) {
                Object.values(children)
                    .filter((child) => ZodJsonSchema.isNode(child))
                    .forEach((child) => ZodJsonSchema.forEachNode(child, visit));
            }
        }
    }
    static isNode(value) {
        return typeof value === 'object' && value !== null && !Array.isArray(value);
    }
    static isPlainStringKeys(value) {
        return ZodJsonSchema.isNode(value) && Object.keys(value).length === 1 && value.type === 'string';
    }
    static matchesNothing(node) {
        const entries = Object.entries(node);
        return (entries.length === 1 &&
            entries[0][0] === 'not' &&
            ZodJsonSchema.isNode(entries[0][1]) &&
            Object.keys(entries[0][1]).length === 0);
    }
}
exports.ZodJsonSchema = ZodJsonSchema;
//# sourceMappingURL=zod-json-schema.js.map